/**
 * CaseFileHeader Component
 *
 * Header for CaseFile page with care recipient info, stats, and action buttons.
 * Based on CASEFILE_FUNCTIONAL_SPEC.md Section 1.
 *
 * When the CaseFile is locked (isLocked=true), write actions (upload, edit,
 * manage team, archive) are disabled with a tooltip explaining why.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  Download,
  MoreVertical,
  Edit,
  Archive,
  Users,
  Settings,
  ArrowLeft,
  BookOpen,
  Lock,
} from 'lucide-react';
import { getLockInlineMessage } from '../../utils/lockMessages';

type CaseFilePermission = 'owner' | 'editor' | 'logger' | 'viewer';

/** Returns true if the permission level allows write actions (upload, edit) */
function hasWriteAccess(permission: CaseFilePermission): boolean {
  return permission === 'owner' || permission === 'editor';
}

interface CaseFileHeaderProps {
  caseFileId: string;
  name: string;
  documentCount: number;
  lastUpdated?: Date;
  isArchived?: boolean;
  careTeamCount?: number;
  isOwner?: boolean;
  userPermission?: CaseFilePermission;
  isLocked?: boolean;
  lockReason?: string;
  onUploadClick?: () => void;
  onExportClick?: () => void;
  onEditClick?: () => void;
  onArchiveClick?: () => void;
  onManageTeamClick?: () => void;
}

export function CaseFileHeader({
  caseFileId,
  name,
  documentCount,
  lastUpdated,
  isArchived = false,
  careTeamCount = 0,
  isOwner = false,
  userPermission = 'viewer',
  isLocked = false,
  lockReason,
  onUploadClick,
  onExportClick,
  onEditClick,
  onArchiveClick,
  onManageTeamClick,
}: CaseFileHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const [showMoreMenu, setShowMoreMenu] = useState(false);

  const lockTooltip = isLocked ? getLockInlineMessage(lockReason) : undefined;
  const isWriteDisabled = isArchived || isLocked;

  // Format last updated
  const formatLastUpdated = (date?: Date): string => {
    if (!date) return 'Never updated';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Updated today';
    if (diffDays === 1) return 'Updated yesterday';
    if (diffDays < 7) return `Updated ${diffDays} days ago`;
    if (diffDays < 30) return `Updated ${Math.floor(diffDays / 7)} weeks ago`;
    return `Updated ${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <div className="bg-white border-b border-soft-neutral">
      <div className="px-4 lg:px-6 py-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left: Back button + Care recipient info */}
          <div className="flex items-start gap-4">
            <button
              onClick={() => navigate('/')}
              className="btn-ghost p-2 -ml-2"
              aria-label="Back to Dashboard"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>

            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-display text-2xl text-deep-forest font-light">
                  {name}
                </h1>
                {isArchived && (
                  <span className="badge badge-secondary">Archived</span>
                )}
                {isLocked && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                    <Lock className="w-3 h-3" />
                    Locked
                  </span>
                )}
              </div>
              <p className="text-sm text-muted mt-1">
                {documentCount} document{documentCount !== 1 ? 's' : ''} • {formatLastUpdated(lastUpdated)}
                {careTeamCount > 0 && ` • ${careTeamCount} care team member${careTeamCount !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>

          {/* Right: Action buttons */}
          <div className="flex items-center gap-2 ml-12 lg:ml-0">
            {/* Upload Document - Primary CTA (only for users with write access) */}
            {hasWriteAccess(userPermission) && (
              <button
                onClick={isWriteDisabled ? undefined : onUploadClick}
                className={`flex items-center gap-2 ${
                  isWriteDisabled
                    ? 'btn-primary opacity-50 cursor-not-allowed'
                    : 'btn-primary'
                }`}
                disabled={isWriteDisabled}
                title={lockTooltip}
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Upload Document</span>
                <span className="sm:hidden">Upload</span>
              </button>
            )}

            {/* Care Logs */}
            <button
              onClick={() => navigate(`/casefile/${caseFileId}/carelogs`)}
              className="btn-ghost flex items-center gap-2"
              title="View care logs"
            >
              <BookOpen className="w-4 h-4" />
              <span className="hidden lg:inline">Care Logs</span>
            </button>

            {/* Export All */}
            <button
              onClick={onExportClick}
              className="btn-ghost flex items-center gap-2"
              title="Export all documents"
            >
              <Download className="w-4 h-4" />
              <span className="hidden lg:inline">Export</span>
            </button>

            {/* More Actions Menu */}
            {isOwner && (
              <div className="relative">
                <button
                  onClick={() => setShowMoreMenu(!showMoreMenu)}
                  className="btn-ghost p-2"
                  aria-label="More actions"
                >
                  <MoreVertical className="w-5 h-5" />
                </button>

                {showMoreMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowMoreMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-soft-neutral py-1 z-20">
                      <button
                        onClick={() => {
                          if (isLocked) return;
                          setShowMoreMenu(false);
                          onEditClick?.();
                        }}
                        disabled={isLocked}
                        title={lockTooltip}
                        className={`w-full text-left px-4 py-2 text-sm flex items-center gap-3 transition-colors ${
                          isLocked
                            ? 'text-muted/50 cursor-not-allowed'
                            : 'text-ink hover:bg-soft-neutral'
                        }`}
                      >
                        <Edit className="w-4 h-4" />
                        Edit CaseFile Info
                        {isLocked && <Lock className="w-3 h-3 ml-auto text-amber-500" />}
                      </button>

                      <button
                        onClick={() => {
                          setShowMoreMenu(false);
                          onManageTeamClick?.();
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-ink hover:bg-soft-neutral transition-colors flex items-center gap-3"
                      >
                        <Users className="w-4 h-4" />
                        Manage Care Team
                        {isLocked && <Lock className="w-3 h-3 ml-auto text-amber-500" />}
                      </button>

                      <button
                        onClick={() => {
                          setShowMoreMenu(false);
                          navigate(`/casefile/${caseFileId}/settings`);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-ink hover:bg-soft-neutral transition-colors flex items-center gap-3"
                      >
                        <Settings className="w-4 h-4" />
                        View Sharing Settings
                      </button>

                      <div className="border-t border-soft-neutral my-1" />

                      <button
                        onClick={() => {
                          if (isLocked) return;
                          setShowMoreMenu(false);
                          onArchiveClick?.();
                        }}
                        disabled={isLocked}
                        title={lockTooltip}
                        className={`w-full text-left px-4 py-2 text-sm flex items-center gap-3 transition-colors ${
                          isLocked
                            ? 'text-muted/50 cursor-not-allowed'
                            : 'text-amber-700 hover:bg-amber-50'
                        }`}
                      >
                        <Archive className="w-4 h-4" />
                        {isArchived ? 'Restore CaseFile' : 'Archive CaseFile'}
                        {isLocked && <Lock className="w-3 h-3 ml-auto text-amber-500" />}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CaseFileHeader;
